import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import _ from "lodash";
import React from "react";
import { useForm } from "react-hook-form";
import SearchInput from "../../components/SearchInput";
import { useVendorStore } from "../../services/vendors";
import { Vendor } from "../../types/vendors";
import { omitEmptyOptionalFields } from "../../utils/helper";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const defaultValue = {
  name: "",
  phone: "",
  email: "",
  integration: "labstack" as const,
  description: "",
  termsAndConditions: "",
  company: [],
  external: true,
  isActive: true,
}

const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index ? children : null}
  </Box>
);

const AddVendorDialog = ({ isModalOpen, toggleModal, selectedId }: any) => {
  const { onCreate, detail, onUpdate } = useVendorStore();
  const [data, setData] = React.useState<Vendor>(defaultValue);
  const [tabValue, setTabValue] = React.useState(0);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<Vendor>();

  const handleChange = (key: any, value: any) => setData(prev => ({ ...prev, [key]: value }));
  const handleClickOpen = () => toggleModal(true);
  const handleClose = () => toggleModal(false);

  const onSubmit = async () => {
    try {

      let response: Vendor | null = null;
      const payload: any = omitEmptyOptionalFields(_.cloneDeep(data));

      delete payload.company;
      delete payload.external;
      if (data?._id) {
        payload.termsAndConditions = data.termsAndConditions?.trim() || "";
      }

      if (data?._id) {
        response = await onUpdate(payload);
      } else {
        response = await onCreate(payload);
      }



      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDetail = async (selectedId: string) => {
    try {
      const res = await detail(selectedId);
      const data = res?.data;
      reset(data);
      setData(data);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    setData(defaultValue);
    setTabValue(0);
    if (selectedId) {
      fetchDetail(selectedId);
    }
  }, [selectedId]);

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <SearchInput />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleClickOpen}
        >
          Add Vendor
        </Button>
      </Stack>

      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "70vh" } }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Add Vendor</DialogTitle>
          <DialogContent dividers>
            <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} aria-label="vendor tabs">
              <Tab label="Vendor Details" />
              <Tab label="Terms and Conditions" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Stack spacing={2}>
                <TextField
                  id="name"
                  label="Full Name"
                  type="name"
                  fullWidth
                  variant="outlined"
                  value={data.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />

                <TextField
                  id="phone"
                  label="Phone"
                  fullWidth
                  variant="outlined"
                  value={data.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />

                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={data.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                />

                <TextField
                  select
                  id="integration"
                  label="Integration"
                  fullWidth
                  variant="outlined"
                  value={data.integration}
                  onChange={(e) => handleChange("integration", e.target.value)}
                >
                  <MenuItem value="labstack">Labstack</MenuItem>
                  <MenuItem value="healthians">Healthians</MenuItem>
                  <MenuItem value="betacura">Betacura</MenuItem>
                </TextField>

                <TextField
                  id="description"
                  label="Description"
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  value={data.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data.isActive !== false}
                      onChange={(e) => handleChange("isActive", e.target.checked)}
                      name="isActive"
                    />
                  }
                  label="Active"
                />
              </Stack>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <TextField
                id="termsAndConditions"
                label="Terms and Conditions"
                fullWidth
                multiline
                minRows={14}
                variant="outlined"
                value={data.termsAndConditions || ""}
                onChange={(e) => handleChange("termsAndConditions", e.target.value)}
                helperText={data._id ? `/api/v1/public/vendors/${data._id}/terms` : "Save vendor to get public terms link"}
              />
            </TabPanel>



          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">Submit</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default AddVendorDialog;
